import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import hrService from '../../../services/hrService';
import { formatEasternDate } from '../../../utils/timezone';

const AdminMetrics = () => {
  const [metrics, setMetrics] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    pendingLeaveRequests: 0,
    attendanceToday: 0,
    overdueTargets: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    setLoading(true);
    setError('');
    try {
      // Load actual data from Supabase
      const data = await hrService?.getAdminDashboardMetrics();
      if (data && typeof data === 'object') {
        setMetrics({
          totalEmployees: data?.totalEmployees || 0,
          activeEmployees: data?.activeEmployees || data?.totalEmployees || 0,
          pendingLeaveRequests: data?.pendingLeaveRequests || 0,
          attendanceToday: data?.attendanceToday || 0,
          overdueTargets: data?.overdueTargets || 0
        });
      } else {
        // Use fallback data if service returns invalid data
        setMetrics({
          totalEmployees: 0,
          activeEmployees: 0,
          pendingLeaveRequests: 0,
          attendanceToday: 0,
          overdueTargets: 0
        });
      }
    } catch (err) {
      console.error('Error loading admin metrics:', err);
      setError('Failed to load dashboard metrics');
      // Keep default values instead of blocking UI
      setMetrics({
        totalEmployees: 0,
        activeEmployees: 0,
        pendingLeaveRequests: 0,
        attendanceToday: 0,
        overdueTargets: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const metricCards = [
    {
      id: 'total-employees',
      title: 'Total Employees',
      value: metrics?.totalEmployees || 0,
      icon: 'Users',
      color: 'primary',
      change: null,
      description: 'All active employees'
    },
    {
      id: 'leaves-notification',
      title: 'Leaves Notification',
      value: metrics?.pendingLeaveRequests || 0,
      icon: 'Bell',
      color: 'warning',
      change: null,
      description: 'Pending leave requests'
    },
    {
      id: 'attendance-today',
      title: "Today\'s Attendance",
      value: metrics?.attendanceToday || 0,
      icon: 'CheckCircle',
      color: 'success',
      change: null,
      description: 'Employees present today'
    },
    {
      id: 'active-employees',
      title: 'Active Employees',
      value: metrics?.activeEmployees || 0,
      icon: 'UserCheck',
      color: 'success',
      change: null,
      description: 'Currently active staff'
    },
    {
      id: 'overdue-targets',
      title: 'Overdue Targets',
      value: metrics?.overdueTargets || 0,
      icon: 'AlertTriangle',
      color: 'destructive',
      change: null,
      description: 'Targets past due date'
    }
  ];

  const getColorClasses = (color) => {
    switch (color) {
      case 'primary':
        return {
          bg: 'bg-primary/10',
          text: 'text-primary',
          icon: 'var(--color-primary)'
        };
      case 'success':
        return {
          bg: 'bg-green-100',
          text: 'text-green-600',
          icon: 'rgb(34, 197, 94)'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-100',
          text: 'text-yellow-600',
          icon: 'rgb(234, 179, 8)'
        };
      case 'destructive':
        return {
          bg: 'bg-red-100',
          text: 'text-red-600',
          icon: 'rgb(239, 68, 68)'
        };
      default:
        return {
          bg: 'bg-gray-100',
          text: 'text-gray-600',
          icon: 'rgb(107, 114, 128)'
        };
    }
  };

  const renderLoadingCard = () => (
    <div className="bg-card rounded-lg border border-border p-6 animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <div className="w-20 h-4 bg-muted rounded"></div>
        <div className="w-8 h-8 bg-muted rounded"></div>
      </div>
      <div className="w-12 h-8 bg-muted rounded mb-2"></div>
      <div className="w-24 h-3 bg-muted rounded"></div>
    </div>
  );

  // Always render the dashboard metrics container to prevent blank page
  return (
    <div className="bg-card rounded-lg border border-border card-shadow p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Dashboard Metrics</h2>
        <button
          onClick={loadMetrics}
          disabled={loading}
          className="p-2 rounded-md hover:bg-background/50 transition-colors disabled:opacity-50"
          aria-label="Reload metrics"
        >
          <Icon 
            name={loading ? "Loader2" : "RefreshCw"} 
            size={20} 
            className={loading ? "animate-spin" : ""}
          />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {loading ? (
          // Show loading cards
          Array.from({ length: 5 })?.map((_, index) => (
            <div key={index}>{renderLoadingCard()}</div>
          ))
        ) : error ? (
          // Show error message in place of metrics
          <div className="col-span-full">
            <div className="text-center py-4">
              <Icon name="AlertCircle" size={24} color="var(--color-destructive)" className="mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Unable to load metrics. Showing current values.</p>
            </div>
          </div>
        ) : null}
        
        {/* Always show metric cards with default or loaded values */}
        {metricCards?.map((metric) => {
          const colors = getColorClasses(metric?.color);
          
          return (
            <div
              key={metric?.id}
              className="bg-card rounded-lg border border-border p-4 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-muted-foreground">
                  {metric?.title}
                </p>
                <div className={`w-8 h-8 rounded-lg ${colors?.bg} flex items-center justify-center`}>
                  <Icon 
                    name={metric?.icon} 
                    size={16} 
                    color={colors?.icon}
                  />
                </div>
              </div>
              <div className="mb-2">
                <p className={`text-2xl font-bold ${colors?.text}`}>
                  {(metric?.value || 0)?.toLocaleString()}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                {metric?.description}
              </p>
              {metric?.change && (
                <div className="flex items-center mt-2">
                  <Icon 
                    name={metric?.change?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} 
                    size={14} 
                    color={metric?.change?.trend === 'up' ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'}
                    className="mr-1"
                  />
                  <span className={`text-xs ${metric?.change?.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                    {metric?.change?.percentage}% from last month
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 text-xs text-muted-foreground text-center">
        Last updated: {formatEasternDate(new Date())}
      </div>
    </div>
  );
};

export default AdminMetrics;